/**
 * SceneSchedule — kiosk scene showing today's dosing schedule.
 * Reads the same server-backed `dosing-schedule` user-data namespace as
 * `/dosing-schedule` (read-only here). Doses whose recurrence fires today
 * are listed first and highlighted; the rest of the schedule follows.
 * Degrades gracefully when the agent is unreachable or nothing is scheduled.
 */
import {
  EMPTY_DOSING_SCHEDULE_DATA,
  describeRecurrence,
  recurrenceDueOn,
  type DosingScheduleData,
  type DosingScheduleEntry,
} from '@reefapp/shared';

import { localTodayIso } from '../../../calendar/localDate';
import { useServerBackedState } from '../../../hooks/useServerBackedState';

/** A simplified schedule item for display purposes. */
interface ScheduleItem {
  id: string;
  label: string;
  detail: string;
  dueToday: boolean;
}

function buildItems(
  entries: readonly DosingScheduleEntry[],
  todayIso: string,
): ScheduleItem[] {
  return entries
    .map((entry) => {
      const dueToday = recurrenceDueOn(entry.recurrence, todayIso);
      const when = describeRecurrence(entry.recurrence);
      return {
        id: entry.id,
        label: `${entry.productName} — ${String(entry.amountMl)} ml`,
        detail: entry.time ? `${when} · ${entry.time}` : when,
        dueToday,
      };
    })
    .sort((a, b) => {
      // Due-today doses first, then alphabetically by label.
      if (a.dueToday && !b.dueToday) return -1;
      if (!a.dueToday && b.dueToday) return 1;
      return a.label.localeCompare(b.label);
    });
}

export function SceneSchedule() {
  const { value: data, loading } = useServerBackedState<DosingScheduleData>(
    'dosing-schedule',
    EMPTY_DOSING_SCHEDULE_DATA,
  );

  const todayIso = localTodayIso();
  const items = buildItems(data.entries, todayIso);

  const now = new Date().toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div
      className="w-full h-full flex flex-col px-12 py-10 overflow-hidden"
      data-testid="scene-schedule"
    >
      <div className="flex items-baseline justify-between mb-8 flex-none">
        <h2 className="text-2xl font-semibold tracking-widest text-white/60 uppercase select-none">
          Today's Schedule
        </h2>
        <span className="num text-sm text-white/30 tabular-nums">{now}</span>
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-white/30 text-xl select-none">Loading…</p>
        </div>
      ) : items.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-white/30 text-xl select-none">
            No scheduled items
          </p>
        </div>
      ) : (
        <ol
          className="flex-1 overflow-y-auto space-y-4"
          aria-label="Today's schedule items"
        >
          {items.map((item) => (
            <li
              key={item.id}
              className="flex items-center gap-4"
              data-testid="schedule-item"
            >
              <span
                className={`w-2.5 h-2.5 rounded-full flex-none ${item.dueToday ? 'bg-[color:var(--accent)]' : 'bg-white/20'}`}
                aria-hidden="true"
              />
              <span className="text-xl text-white font-medium">{item.label}</span>
              <span className="text-base text-white/50 ml-auto">{item.detail}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
